// @internal
import './window-operations';

import {Component, ComponentRegistry} from './component-registry-service';

let useKeyboardShortcuts = true;
let tabComponents: Array<TabComponent> = [];
let savedState: Array<number> = [];
let selectedTabComponent: TabComponent;

(function ()
{
	let state = window.getParameter('_tab').split('_') as Array<string>;

	for (let i = 0; i < state.length; i++)
	{
		savedState.push(parseInt(state[i]));
	}
})();

function updateState()
{
	let value = '';

	for (let i = 0; i < tabComponents.length; i++)
	{
		if (i > 0)
		{
			value += '_';
		}

		value += tabComponents[i].getCurrentIndex() + 1;
	}

	window.setParameter('_tab', value);
}

class TabElement
{
	buttonElement: HTMLDivElement;
	contentElement: HTMLDivElement;
	focusElement: HTMLElement | null = null;
}

export function setUseKeyboardShortcuts(b: boolean)
{
	useKeyboardShortcuts = b;
}

export default class TabComponent extends Component
{
	private readonly containerElement: HTMLDivElement;
	private readonly headerElement: HTMLDivElement;
	private readonly tabElements: Array<TabElement>;
	private readonly observer: MutationObserver;
	private activeTabElement: TabElement;

	constructor(containerElement: HTMLDivElement)
	{
		super();


		if (!(containerElement instanceof HTMLDivElement))
		{
			throw new Error('containerElement is not an HTMLDivElement');
		}

		if (!selectedTabComponent)
		{
			selectedTabComponent = this;
		}

		containerElement.addEventListener('mousedown', this.onFocus.bind(this), {passive: true});

		this.containerElement = containerElement;
		this.tabElements = [];

		this.headerElement = document.createElement('DIV') as HTMLDivElement;
		this.headerElement.classList.add('for-tab-header');
		this.headerElement.addEventListener('click', this.onClick.bind(this));

		this.containerElement.prepend(this.headerElement);

		let elements = containerElement.querySelectorAll('div.for-tab-content');

		for (let i = 0; i < elements.length; i++)
		{
			this.attach(elements[i] as HTMLDivElement);
		}

		this.observer = new MutationObserver(function (this: TabComponent, mutationsList: MutationRecord[])
		{
			for (let i = 0; i < mutationsList.length; i++)
			{
				if (mutationsList[i].type == 'childList')
				{
					mutationsList[i].addedNodes.forEach(function (this: TabComponent, value: Node)
					{
						if ((value instanceof HTMLDivElement) && value.classList.contains('for-tab-content'))
						{
							this.attach(value);
						}
					}.bind(this));

					mutationsList[i].removedNodes.forEach(function (this: TabComponent, value: Node)
					{
						if ((value instanceof HTMLDivElement) && value.classList.contains('for-tab-content'))
						{
							this.detach(value);
						}
					}.bind(this));
				}
			}
		}.bind(this));

		this.observer.observe(containerElement, {childList: true, subtree: true});

		tabComponents.push(this);

		let index = 0;

		if (savedState && savedState.length >= tabComponents.length)
		{
			index = savedState[tabComponents.length - 1];

			if (isNaN(index))
			{
				index = 0;
			}
			else
			{
				index = index - 1;
			}
		}

		this.select(this.tabElements.length > index ? this.tabElements[index] : this.tabElements[0]);
	}

	destroy()
	{
		this.containerElement.removeEventListener('mousedown', this.onFocus.bind(this));

		let index = tabComponents.indexOf(this);
		if (index > -1)
		{
			tabComponents.splice(index, 1);
		}
	}

	private onFocus()
	{
		selectedTabComponent = this;
	}

	private onClick(e: MouseEvent)
	{
		let target = e.target as Node | null;

		while (target && !((target instanceof HTMLDivElement) && target.classList.contains('for-tab-button')))
		{
			target = target.parentNode;
		}

		let tabElement = this.getTabElement(target);

		if (tabElement)
		{
			this.select(tabElement);
		}
	}

	private select(tabElement: TabElement)
	{
		if (this.activeTabElement)
		{
			let element = document.activeElement;

			this.activeTabElement.focusElement = null;

			if (element)
			{
				let parentNode = element.parentNode;

				while (parentNode && parentNode != this.activeTabElement.contentElement)
				{
					parentNode = parentNode.parentNode;
				}

				if (parentNode && parentNode == this.activeTabElement.contentElement)
				{
					this.activeTabElement.focusElement = element as HTMLElement;
				}
			}

			this.activeTabElement.buttonElement.classList.remove('active');
			this.activeTabElement.contentElement.style.display = 'none';
		}

		this.activeTabElement = tabElement;

		if (this.activeTabElement)
		{
			this.activeTabElement.buttonElement.classList.add('active');
			this.activeTabElement.contentElement.style.display = '';

			setTimeout(() =>
			{
				window.dispatchEvent(new Event('resize'));

				if (this.activeTabElement.focusElement)
				{
					this.activeTabElement.focusElement.focus();
					//try { this.activeTabElement.focusElement.select(); } catch (ex){}
				}
				else
				{
					let autoFocusElement = this.activeTabElement.contentElement.querySelector('[autofocus], .autofocus');

					if (autoFocusElement)
					{
						(autoFocusElement as HTMLElement).focus();
						try
						{ (autoFocusElement as HTMLInputElement).select(); }
						catch (ex)
						{}
					}
					else
					{
						let autoFocusElements = this.activeTabElement.contentElement.querySelectorAll('input, textarea');

						for (let i = 0; i < autoFocusElements.length; i++)
						{
							if (autoFocusElements[i].clientWidth > 0)
							{
								(autoFocusElements[i] as HTMLElement).focus();
								try
								{ (autoFocusElements[i] as HTMLInputElement).select(); }
								catch (ex)
								{}
								break;
							}
						}
					}
				}
			}, 0);

			updateState();
		}
	}

	private getTabElement(element: HTMLDivElement | Node | null)
	{
		if (element)
		{
			for (let i = 0; i < this.tabElements.length; i++)
			{
				if (this.tabElements[i].contentElement == element || this.tabElements[i].buttonElement == element)
				{
					return this.tabElements[i];
				}
			}
		}

		return null;
	}

	private attach(contentElement: HTMLDivElement)
	{
		let tabElement = this.getTabElement(contentElement);

		if (!tabElement)
		{
			tabElement = new TabElement();

			tabElement.contentElement = contentElement;
			contentElement.style.display = 'none';
			tabElement.buttonElement = document.createElement('DIV') as HTMLDivElement;
			tabElement.buttonElement.classList.add('for-tab-button');

			let numberElement = document.createElement('SPAN') as HTMLSpanElement;
			numberElement.classList.add('for-tab-button-number');
			numberElement.innerText = (this.tabElements.length + 1).toString();

			let labelElement = document.createElement('SPAN') as HTMLSpanElement;
			labelElement.classList.add('for-tab-button-label');
			labelElement.innerText = contentElement.getAttribute('data-title') as string;

			tabElement.buttonElement.appendChild(numberElement);
			tabElement.buttonElement.appendChild(labelElement);

			this.headerElement.appendChild(tabElement.buttonElement);

			this.tabElements.push(tabElement);
		}
	}

	private detach(contentElement: HTMLDivElement)
	{
		let tabElement = this.getTabElement(contentElement);

		if (tabElement)
		{
			tabElement.buttonElement.remove();

			let index = this.tabElements.indexOf(tabElement);

			if (index > -1)
			{
				this.tabElements.splice(index, 1);
			}
		}
	}

	switchTo(index: number)
	{
		if (index < this.tabElements.length)
		{
			this.select(this.tabElements[index]);
		}
	}

	getCurrentIndex()
	{
		return this.activeTabElement ? this.tabElements.indexOf(this.activeTabElement) : 0;
	}

	getTabElements()
	{
		return this.tabElements;
	}
}

window.addEventListener('keydown', function (e)
{
	if (useKeyboardShortcuts && selectedTabComponent)
	{
		if (e.altKey && e.key.length == 1 && e.location !== 3 /* DOM_KEY_LOCATION_NUMPAD */)
		{
			let number = parseInt(e.key);

			if (!isNaN(number) && number > 0 && number <= 9)
			{
				e.preventDefault();
				e.stopPropagation();

				selectedTabComponent.switchTo(number - 1);

				return false;
			}
		}
		else if (!e.altKey && !e.metaKey && !e.ctrlKey && (e.key == 'F7' || e.key == 'F8'))
		{
			e.preventDefault();
			e.stopPropagation();

			let elements = selectedTabComponent.getTabElements();
			let index = selectedTabComponent.getCurrentIndex();

			if (e.key == 'F7')
			{
				index--;
			}
			else
			{
				index++;
			}

			if (index >= elements.length)
			{
				index = 0;
			}
			else if (index < 0)
			{
				index = elements.length - 1;
			}

			selectedTabComponent.switchTo(index);

			return false;
		}
	}

	return true;
});

ComponentRegistry.register(TabComponent, 'div', 'for-tab');
